import React, { FC, Fragment, useMemo, useState, useCallback } from 'react';
import {
  Typography,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Icon,
  CircularProgress,
  LinearProgress,
} from '@material-ui/core';
import { ArrowDownward, ArrowUpward } from '@material-ui/icons';
import { Tooltip, Button } from '@ccm-office/ccm-ui';
import classNames from 'classnames';
import { DEFAULT_SORT_DIRECTION } from 'enums/sorting';
import { getComparator, getNextOrder, sortData } from 'utils/sorting';
import { TSortBy, IDataTableProps } from './types';
import { useStyles } from './styles';

const emptyDataMessage = 'No Records to View';

export const DataTable: FC<IDataTableProps> = ({
  title,
  tools = [],
  columns = [],
  data = [],
  isRowSelected,
  onRowClick,
  actions = [],
  error,
  detailsPanel,
  showDetailsPanel,
  dataItemIdPropName,
  defaultSortBy,
  disableSorting = false,
  isFormTable = false,
  cssClass,
  loading,
}) => {
  const classes = useStyles();
  const [sortBy, setSortBy] = useState<TSortBy>({
    field: defaultSortBy,
    direction: DEFAULT_SORT_DIRECTION,
  });

  const { field: sortByField, direction: sortDirection } = sortBy;
  const extractDataItemsIds = () => {
    if (!dataItemIdPropName) return [];

    return data.map((item) => {
      return item[dataItemIdPropName];
    });
  };

  const dataItemsIds = extractDataItemsIds();

  const sortedData = useMemo(() => {
    if (sortByField && sortDirection && data) {
      const excludedData = data.filter(({ excludeFromSorting = false }) => excludeFromSorting);
      const sortableData = data.filter(({ excludeFromSorting = false }) => !excludeFromSorting);

      return [
        ...excludedData,
        ...sortData(sortableData, getComparator(sortDirection, sortByField)),
      ];
    }

    return data;
  }, [data, sortByField, sortDirection]);

  const onColumnClick = useCallback(
    (column: string) => {
      if (sortByField !== column) {
        setSortBy({ field: column, direction: DEFAULT_SORT_DIRECTION });
      } else {
        const nextDirection = getNextOrder(sortDirection);

        if (nextDirection) {
          setSortBy({ ...sortBy, direction: nextDirection });
        } else {
          setSortBy({});
        }
      }
    },
    [sortBy, sortByField, sortDirection]
  );

  const renderColumns = useMemo(
    () =>
      columns.map((column, i) => {
        const sortingField = column.sortingField || column.name;
        const isColumnSorted = sortByField === sortingField;
        return (
          <TableCell
            key={i}
            data-testid={`header-column-${i}`}
            sortDirection={isColumnSorted ? sortDirection : false}
          >
            <TableSortLabel
              direction={sortDirection}
              disabled={disableSorting}
              onClick={() => onColumnClick(sortingField)}
              IconComponent={() => (
                <span
                  className={classes.sortIcon}
                  style={{ visibility: isColumnSorted ? 'visible' : 'hidden' }}
                >
                  {sortDirection === 'desc' ? <ArrowDownward /> : <ArrowUpward />}
                </span>
              )}
            >
              {column.label}
            </TableSortLabel>
          </TableCell>
        );
      }),
    [columns, disableSorting, onColumnClick, sortByField, sortDirection, classes.sortIcon]
  );

  const renderData = useMemo(
    () =>
      sortedData
        ? sortedData.map((row, rowIndex) => {
            return (
              <Fragment key={rowIndex}>
                <TableRow
                  className={classNames({
                    'form-row': row.excludeFromSorting || isFormTable || row.isEditing,
                    edit: row.isEditing,
                  })}
                  hover={onRowClick != null}
                  selected={isRowSelected && isRowSelected(row)}
                  data-testid={`row-${rowIndex}`}
                  onClick={() => onRowClick && onRowClick(row, rowIndex)}
                >
                  {columns.map((column, columnIndex) => {
                    const value = row[column.name];

                    return (
                      <TableCell
                        key={columnIndex}
                        data-testid={`row-${rowIndex}-col-${columnIndex}`}
                        className={column.disableOnEdit ? 'not-editable' : ''}
                      >
                        {column.render
                          ? column.render({
                              value,
                              rowIndex,
                            })
                          : value}
                      </TableCell>
                    );
                  })}
                  {actions.length > 0 && (
                    <TableCell
                      className="action-cell"
                      data-testid={`edit-row-${rowIndex}`}
                      width={isFormTable ? '46px' : '100px'}
                      align="right"
                    >
                      {actions.map(
                        ({ label, onClick, icon, visible, loading }, j) =>
                          (visible === undefined || visible(rowIndex, row)) && (
                            <Tooltip key={j} title={label}>
                              <IconButton
                                className="action-button"
                                data-testid={`action-button-${label}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onClick(row, rowIndex, dataItemsIds);
                                }}
                              >
                                <Icon>{icon}</Icon>
                                {loading && (
                                  <CircularProgress
                                    size={50}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                    }}
                                  />
                                )}
                              </IconButton>
                            </Tooltip>
                          )
                      )}
                    </TableCell>
                  )}
                </TableRow>
                {showDetailsPanel && showDetailsPanel(row, rowIndex) && detailsPanel && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1}>
                      {detailsPanel(row, rowIndex)}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })
        : null,
    [
      sortedData,
      onRowClick,
      isRowSelected,
      columns,
      actions,
      showDetailsPanel,
      detailsPanel,
      dataItemsIds,
      isFormTable,
    ]
  );

  return (
    <Fragment>
      <div className={classes.headerContainer}>
        <Typography data-testid="table-title" className="data-table-title" variant="h3">
          {title}
        </Typography>

        {tools.map(({ text, label, icon, onClick, disabled }) => {
          return (
            <Button
              key={text}
              onClick={onClick}
              variant="link"
              disabled={Boolean(disabled)}
              startIcon={typeof icon === 'string' ? <Icon>{icon}</Icon> : icon}
              data-testid={`data-table-button-${label}`}
            >
              {text}
            </Button>
          );
        })}
      </div>
      {error && typeof error === 'string' && <div className={classes.error}>{error}</div>}

      <Table className={cssClass}>
        <TableHead>
          <TableRow>
            {renderColumns}
            {actions.length > 0 && <TableCell />}
          </TableRow>
          {loading && (
            <TableRow>
              <TableCell
                className={classes.progressContainer}
                colSpan={columns.length + 1}
                padding="none"
              >
                <LinearProgress className={classes.progress} color="secondary" />
              </TableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {(sortedData && sortedData.length && renderData) || (
            <TableRow className={classes.emptyData}>
              <TableCell colSpan={columns.length + 1}>{emptyDataMessage}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Fragment>
  );
};
